/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import React, {memo} from 'react';
import {makeStyles} from '@material-ui/core/styles';
import classnames from 'classnames';
import isString from 'lodash.isstring';
import {translate} from '@ion/core/src/locale/locale';
import PulseLoader from '../components/loading/PulseLoader';
import {MaterialAddIcon} from '../icons/current/MaterialAddIcon';
import {MaterialRemoveIcon} from '../icons/current/MaterialRemoveIcon';
import {TopNavSearchIcon} from '../icons/current/TopNavSearchIcon';
import {MaterialExitCloseIcon} from '../icons/MaterialExitCloseIcon';
import Render from '../render/Render';
import {dlsColor} from '../theme/theme';
import {IconSize} from '../types/types';
import FilterChecklistItem from './FilterChecklistItem';
import {
  FilterChecklistOptionBase,
  FilterChecklistPropsBase,
  FilterChecklistValueBase
} from './types';
import useFilterChecklist from './useFilterChecklist';
import useFilterChecklistLayoutAnimation from './useFilterChecklistLayoutAnimation';
import {getFilterChecklistValueKey} from './utils';

export type FilterChecklistValue = FilterChecklistValueBase;

export interface FilterChecklistOption extends FilterChecklistOptionBase {}

export interface FilterChecklistProps<
  T extends FilterChecklistOption = FilterChecklistOption
> extends FilterChecklistPropsBase<T> {}

const useStyles = makeStyles(theme => ({
  root : {
    boxSizing     : 'border-box',
    display       : 'flex',
    flexDirection : 'column',
    width         : '100%'
  },
  searchBar : {
    alignItems     : 'center',
    boxSizing      : 'border-box',
    display        : 'flex',
    flexDirection  : 'column',
    gap            : 10,
    height         : 60,
    justifyContent : 'center',
    padding        : '8px 16px',
    width          : '100%'
  },
  searchBarActive : {
    gap    : 4,
    height : 84
  },
  searchBox : {
    alignItems       : 'center',
    backgroundColor  : dlsColor('white'),
    border           : `1px solid ${dlsColor('grey5')}`,
    borderRadius     : 8,
    boxSizing        : 'border-box',
    display          : 'flex',
    gap              : 49,
    height           : 44,
    justifyContent   : 'space-between',
    padding          : 16,
    width            : '100%',
    // the focus ring follows real focus, so clicking away clears it. The
    // border keeps its 1px width (the ring's second pixel is an inset
    // shadow), so focusing never shifts the box's content ...
    '&:focus-within' : {
      borderColor : dlsColor('darkTeal'),
      boxShadow   : `inset 0 0 0 1px ${dlsColor('darkTeal')}`
    }
  },
  searchInputGroup : {
    alignItems : 'center',
    display    : 'flex',
    flex       : 1,
    gap        : 8,
    height     : 24,
    minWidth   : 0
  },
  searchInput : {
    backgroundColor  : 'transparent',
    border           : 0,
    boxSizing        : 'border-box',
    color            : dlsColor('grey3'),
    flex             : 1,
    fontFamily       : 'Inter',
    fontSize         : 14,
    height           : 20,
    lineHeight       : '20px',
    minWidth         : 0,
    outline          : 0,
    padding          : 0,
    width            : '100%',
    '&::placeholder' : {
      color   : dlsColor('grey3'),
      opacity : 1
    }
  },
  clearSearchButton : {
    alignItems      : 'center',
    backgroundColor : dlsColor('white'),
    border          : `1px solid ${dlsColor('grey5')}`,
    borderRadius    : 8,
    boxSizing       : 'border-box',
    cursor          : 'pointer',
    display         : 'flex',
    flexShrink      : 0,
    height          : 24,
    justifyContent  : 'center',
    padding         : 0,
    width           : 24,
    '& svg'         : {
      height : 16,
      width  : 16
    }
  },
  searchResultsCount : {
    alignSelf    : 'stretch',
    color        : dlsColor('grey2'),
    fontFamily   : 'Inter',
    fontSize     : 13,
    lineHeight   : '16px',
    paddingRight : 4,
    textAlign    : 'right'
  },
  optionsContainer : {
    alignItems    : 'flex-start',
    boxSizing     : 'border-box',
    display       : 'flex',
    flexDirection : 'column',
    gap           : 10,
    padding       : '24px 16px 0 16px',
    width         : '100%'
  },
  chipsPanel : {
    alignItems      : 'flex-start',
    alignSelf       : 'stretch',
    backgroundColor : dlsColor('grey6'),
    borderRadius    : 12,
    display         : 'flex',
    flexDirection   : 'column',
    width           : '100%'
  },
  loadingPanel : {
    alignItems     : 'center',
    justifyContent : 'center',
    minHeight      : 112
  },
  ctas : {
    alignItems     : 'center',
    boxSizing      : 'border-box',
    display        : 'flex',
    height         : 44,
    justifyContent : 'space-between',
    padding        : '0 16px',
    width          : '100%'
  },
  ctaGroup : {
    alignItems : 'center',
    display    : 'flex',
    gap        : 8
  },
  ctaButton : {
    alignItems      : 'center',
    backgroundColor : 'transparent',
    border          : 0,
    color           : dlsColor('darkestBlue'),
    cursor          : 'pointer',
    display         : 'flex',
    fontFamily      : 'Inter',
    fontSize        : 13,
    fontWeight      : 600,
    height          : 32,
    letterSpacing   : 0.5,
    lineHeight      : '16px',
    padding         : '4px 16px'
  },
  ctaButtonDisabled : {
    color  : dlsColor('grey4'),
    cursor : 'default'
  },
  chips : {
    alignContent  : 'flex-start',
    alignItems    : 'flex-start',
    alignSelf     : 'stretch',
    borderRadius  : 12,
    boxSizing     : 'border-box',
    display       : 'flex',
    flexDirection : 'row',
    flexWrap      : 'wrap',
    gap           : 16,
    padding       : 16,
    width         : '100%'
  },
  // the DLS chip's states - unselected, hovered, selected and disabled ...
  chip : {
    alignItems      : 'center',
    backgroundColor : dlsColor('white'),
    border          : '1px solid transparent',
    borderRadius    : 8,
    boxSizing       : 'border-box',
    color           : dlsColor('grey1'),
    cursor          : 'pointer',
    display         : 'flex',
    fontFamily      : 'Inter',
    fontSize        : 14,
    fontWeight      : 500,
    gap             : 8,
    height          : 32,
    justifyContent  : 'center',
    lineHeight      : '16px',
    maxWidth        : '100%',
    minWidth        : 0,
    padding         : 8,
    transition      : 'border-color 160ms ease, background-color 160ms ease, color 160ms ease',
    '&:hover'       : {
      backgroundColor : dlsColor('aquamarine'),
      borderColor     : dlsColor('skyBlue')
    }
  },
  chipSelected : {
    '&, &:hover' : {
      backgroundColor : dlsColor('paleBlue'),
      borderColor     : 'transparent',
      fontWeight      : 600
    }
  },
  chipDisabled : {
    '&, &:hover' : {
      backgroundColor : dlsColor('grey8'),
      borderColor     : dlsColor('grey4'),
      color           : dlsColor('grey4')
    },
    pointerEvents : 'none'
  },
  chipLabel : {
    display      : 'block',
    maxWidth     : '100%',
    overflow     : 'hidden',
    textOverflow : 'ellipsis',
    whiteSpace   : 'nowrap'
  },
  showAllRow : {
    alignItems     : 'center',
    boxSizing      : 'border-box',
    display        : 'flex',
    gap            : 8,
    height         : 44,
    justifyContent : 'center',
    padding        : '0 16px',
    width          : '100%'
  },
  showAllButton : {
    alignItems      : 'center',
    backgroundColor : 'transparent',
    border          : 0,
    borderRadius    : 4,
    color           : dlsColor('darkestBlue'),
    cursor          : 'pointer',
    display         : 'flex',
    fontFamily      : 'Inter',
    fontSize        : 13,
    fontWeight      : 600,
    gap             : 8,
    height          : 32,
    letterSpacing   : 0.5,
    lineHeight      : '16px',
    padding         : '0 8px',
    '&:disabled'    : {
      color  : dlsColor('grey4'),
      cursor : 'default'
    }
  },
  addIcon : {
    alignItems     : 'center',
    display        : 'flex',
    height         : 16,
    justifyContent : 'center',
    width          : 16,
    '& svg'        : {
      height : 16,
      width  : 16
    }
  },
  message : {
    color      : dlsColor('grey3'),
    fontFamily : 'Inter',
    fontSize   : 14,
    lineHeight : '20px',
    padding    : '8px 2px'
  },
  loading : {
    alignItems     : 'center',
    alignSelf      : 'stretch',
    boxSizing      : 'border-box',
    display        : 'flex',
    justifyContent : 'center',
    minHeight      : 64,
    padding        : 16,
    width          : '100%'
  },
  noResults : {
    alignItems     : 'center',
    alignSelf      : 'stretch',
    boxSizing      : 'border-box',
    color          : dlsColor('grey3'),
    display        : 'flex',
    fontFamily     : 'Inter',
    fontSize       : 14,
    justifyContent : 'center',
    lineHeight     : '20px',
    minHeight      : 64,
    padding        : 16,
    width          : '100%'
  }
}));

const getErrorMessage = (error: boolean | string) => (
  isString(error) ? error : translate('common:errorHandler.message')
);

/**
 * Displays searchable chip selections for a filter drawer category.
 *
 * ```typescript
 * import FilterChecklist from '@ion/dls/src/filters/FilterChecklist';
 *
 * ...
 *
 * <FilterChecklist searchable
 *   options={options}
 *   selectedValues={selectedValues}
 *   searchPlaceholder='Search asset type...'
 *   onChange={handleFilterChange} />
 * ```
 */
export const FilterChecklist = <T extends FilterChecklistOption = FilterChecklistOption>({
  className,
  emptyState,
  error,
  onChange,
  options = [],
  loading = false,
  disabled = false,
  searchable = false,
  searchWhenOverflowing = false,
  selectedValues = [],
  noOptionsLabel = translate('filters:options.noOptions'),
  searchPlaceholder = translate('filters:options.searchPlaceholder'),
  showAllLabel = translate('filters:options.showAll'),
  showLessLabel = translate('filters:options.showLess'),
  visibleCount,
  getOptionLabel = option => option.label,
  getOptionValue = option => option.value
}: FilterChecklistProps<T>) => {
  const classes = useStyles();
  const {
    capturePositions,
    containerRef
  } = useFilterChecklistLayoutAnimation();
  const {
    activateSearch,
    allVisible,
    allVisibleSelected,
    blurSearch,
    closeSearch,
    displayedOptions,
    handleClear,
    handleSearchChange,
    handleSelectAll,
    hasOverflow,
    hasSelections,
    searchActive,
    searching,
    searchValue,
    selectedLookup,
    toggleShowAll,
    toggleOption,
    visibleOptions
  } = useFilterChecklist({
    getOptionLabel,
    getOptionValue,
    onChange,
    options,
    selectedValues,
    visibleCount
  });

  const showSearch        = searchable && (!searchWhenOverflowing || hasOverflow);
  const noSearchResults   = searchActive && !visibleOptions.length;
  const selectAllDisabled = disabled || !visibleOptions.length || allVisibleSelected;
  const clearDisabled     = disabled || !hasSelections || noSearchResults;
  const resultCountLabel  = translate(
    `filters:options.${visibleOptions.length === 1 ? 'result' : 'results'}`,
    {count: visibleOptions.length}
  );

  return (
    <div className={className ? `${classes.root} ${className}` : classes.root}
      onClickCapture={capturePositions}>
      <Render.If condition={showSearch}>
        {/* the bar only grows for the result count under a typed term -
            focusing the empty box leaves it where it is ... */}
        <div className={classnames(classes.searchBar, {
          [classes.searchBarActive] : searching
        })}>
          <div className={classes.searchBox}>
            <div className={classes.searchInputGroup}>
              <TopNavSearchIcon color={dlsColor('grey2')} size={IconSize.medium} />
              <input className={classes.searchInput}
                aria-label={searchPlaceholder}
                value={searchValue}
                placeholder={searchPlaceholder}
                onFocus={activateSearch}
                onBlur={blurSearch}
                onChange={handleSearchChange} />
            </div>
            {/* clear and the match count only once there's a term to clear / count ... */}
            <Render.If condition={searching}>
              <button type='button'
                className={classes.clearSearchButton}
                onClick={closeSearch}
                aria-label={translate('filters:options.closeSearch')}>
                <MaterialExitCloseIcon color={dlsColor('grey1')} size={IconSize.small} />
              </button>
            </Render.If>
          </div>
          <Render.If condition={searching}>
            <div className={classes.searchResultsCount} aria-live='polite'>
              {resultCountLabel}
            </div>
          </Render.If>
        </div>
      </Render.If>
      <Render>
        <Render.If condition={loading}>
          <div className={classes.optionsContainer}>
            <div className={classnames(classes.chipsPanel, classes.loadingPanel)}>
              <div className={classes.loading}>
                <PulseLoader message={translate('common:states.loading')} />
              </div>
            </div>
          </div>
        </Render.If>
        <Render.ElseIf condition={!!error}>
          <div className={classes.message}>{getErrorMessage(error as any)}</div>
        </Render.ElseIf>
        <Render.ElseIf condition={!options.length}>
          {emptyState || <div className={classes.message}>{noOptionsLabel}</div>}
        </Render.ElseIf>
        <Render.Else>
          <div className={classes.optionsContainer}>
            <div className={classes.chipsPanel}>
              <div className={classes.ctas}>
                <div className={classes.ctaGroup}>
                  <button type='button'
                    className={classnames(classes.ctaButton, {
                      [classes.ctaButtonDisabled] : selectAllDisabled
                    })}
                    disabled={selectAllDisabled}
                    onClick={handleSelectAll}>
                    {translate('filters:options.selectAll')}
                  </button>
                  <button type='button'
                    className={classnames(classes.ctaButton, {
                      [classes.ctaButtonDisabled] : clearDisabled
                    })}
                    disabled={clearDisabled}
                    onClick={handleClear}>
                    {translate('filters:options.clear')}
                  </button>
                </div>
              </div>
              <div ref={containerRef} className={classes.chips}>
                <Render.If condition={noSearchResults}>
                  <div className={classes.noResults} role='status'>
                    {translate('filters:options.noResults')}
                  </div>
                </Render.If>
                {displayedOptions.map(option => {
                  const optionValue = getOptionValue(option);
                  const optionKey   = getFilterChecklistValueKey(optionValue);
                  return (
                    <FilterChecklistItem key={optionKey}
                      classes={classes}
                      disabled={disabled}
                      getOptionLabel={getOptionLabel}
                      option={option}
                      optionKey={optionKey}
                      selected={!!selectedLookup[getFilterChecklistValueKey(optionValue)]}
                      onToggle={toggleOption} />
                  );
                })}
              </div>
              {/* inside the grey panel, centred, as every filter's is - and
                  hidden while a typed term shows every match ... */}
              <Render.If condition={hasOverflow && !searching}>
                <div className={classes.showAllRow}>
                  <button type='button'
                    className={classes.showAllButton}
                    aria-expanded={allVisible}
                    onClick={toggleShowAll}>
                    <span className={classes.addIcon}>
                      <Render>
                        <Render.If condition={allVisible}>
                          <MaterialRemoveIcon color={dlsColor('darkestBlue')}
                            size={IconSize.small} />
                        </Render.If>
                        <Render.Else>
                          <MaterialAddIcon color={dlsColor('darkestBlue')}
                            size={IconSize.small} />
                        </Render.Else>
                      </Render>
                    </span>
                    <span>{allVisible ? showLessLabel : showAllLabel}</span>
                  </button>
                </div>
              </Render.If>
            </div>
          </div>
        </Render.Else>
      </Render>
    </div>
  );
};

export default memo(FilterChecklist) as typeof FilterChecklist;
